import React, { useState } from 'react';
import { Home, TrendingUp, DollarSign, Shield, Clock, CheckCircle, ArrowRight, Phone, Loader2, MapPin, BarChart3, Key } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEOHead from '../components/SEOHead';
import { submitLoanInquiry } from '../services/api';

const benefits = [
  { icon: DollarSign, title: 'No W-2 or Tax Returns', desc: 'Qualify based on the property\'s short-term rental income — not your personal income, employment history, or tax returns.' },
  { icon: TrendingUp, title: 'Use Airbnb / VRBO Income', desc: 'We underwrite using actual or projected STR revenue from platforms like Airbnb, VRBO, and Furnished Finder.' },
  { icon: Clock, title: 'Close in as Few as 10 Days', desc: 'Our streamlined process means you can move fast on time-sensitive deals and lock in the best investment properties.' },
  { icon: Shield, title: 'Competitive DSCR Programs', desc: 'Purpose-built loan programs for short-term rental investors with flexible terms, strong LTVs, and investor-friendly structures.' },
  { icon: Home, title: 'Purchase or Refinance', desc: 'Whether you\'re buying your first STR or pulling equity from an existing vacation rental, we have a program for you.' },
  { icon: BarChart3, title: 'Scale Your Portfolio', desc: 'No limit on the number of financed properties. Grow your STR portfolio without the constraints of conventional lending.' },
];

const loanFeatures = [
  { label: 'Loan Amounts', value: '$100K – $5M+' },
  { label: 'LTV', value: 'Up to 80%' },
  { label: 'Loan Terms', value: '30-Year Fixed, 5/1 ARM, Interest Only' },
  { label: 'Property Types', value: 'SFR, Condo, Townhome, 2-4 Unit, Cabins' },
  { label: 'Min. Credit Score', value: '660+' },
  { label: 'Closing Speed', value: 'As fast as 10 business days' },
  { label: 'Income Docs', value: 'None — qualify on rental income' },
  { label: 'Prepayment', value: 'Flexible options available' },
];

const platforms = [
  { name: 'Airbnb', desc: 'The world\'s largest STR marketplace' },
  { name: 'VRBO', desc: 'Vacation rental platform by Expedia' },
  { name: 'Furnished Finder', desc: 'Medium-term rental marketplace' },
  { name: 'Booking.com', desc: 'Global travel & lodging platform' },
];

const ShortTermRentalPage = () => {
  const [formData, setFormData] = useState({
    loanType: 'Short Term Rental',
    firstName: '', lastName: '', email: '', phone: '',
    propertyAddress: '', purchasePrice: '', rehabBudget: '', arv: '',
    exitStrategy: '', message: ''
  });
  const [smsOptIn, setSmsOptIn] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await submitLoanInquiry({
        ...formData,
        purchasePrice: formData.purchasePrice ? parseFloat(formData.purchasePrice) : null,
        rehabBudget: formData.rehabBudget ? parseFloat(formData.rehabBudget) : null,
        arv: formData.arv ? parseFloat(formData.arv) : null,
      });
      setSuccess(true);
    } catch (err) {
      setError('Something went wrong. Please try again or call us directly.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen">
      <SEOHead
        title="Short-Term Rental Loans | Airbnb & VRBO Financing | Close Now Lending"
        description="Finance your Airbnb, VRBO, or vacation rental investment with Close Now Lending. DSCR loans for short-term rentals — no W-2 or tax returns required. Close in as few as 10 days."
        keywords="short term rental loans, STR financing, Airbnb loans, VRBO financing, vacation rental loans, DSCR short term rental, STR DSCR loan, short term rental mortgage, Airbnb investment loan, Close Now Lending STR"
        url="/short-term-rental"
      />
      <Header />

      {/* Hero */}
      <section className="relative bg-slate-900 pt-28 pb-20 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img
            src="https://images.pexels.com/photos/2476632/pexels-photo-2476632.jpeg?auto=compress&cs=tinysrgb&w=1600"
            alt="Vacation rental property"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative z-10 container mx-auto px-4">
          <div className="max-w-3xl">
            <div className="inline-block bg-blue-500/20 border border-blue-400/30 rounded-full px-4 py-1.5 mb-5">
              <span className="text-blue-300 text-sm font-semibold tracking-wide">SHORT-TERM RENTAL FINANCING</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-5 leading-tight" data-testid="str-hero-heading">
              Finance Your Next<br />
              <span className="text-blue-400">Short-Term Rental.</span>
            </h1>
            <p className="text-lg text-gray-300 max-w-2xl leading-relaxed mb-8">
              Close Now Lending Corp specializes in DSCR loans for Airbnb, VRBO, and vacation rental investors. Qualify on the property's rental income — no W-2s, no tax returns, no hassle. Close in as few as 10 business days.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a href="#str-inquiry-form">
                <Button className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 text-base" data-testid="str-hero-cta">
                  <Key className="h-5 w-5 mr-2" />Get Pre-Qualified Now
                </Button>
              </a>
              <a href="tel:+19034762937">
                <Button variant="outline" className="border-white/30 text-white hover:bg-white/10 px-8 py-3 text-base" data-testid="str-hero-call">
                  <Phone className="h-5 w-5 mr-2" />Call 903-476-2937
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Why STR Investors Choose Us */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-base md:text-lg font-bold text-gray-900 mb-3">Why STR Investors Choose Close Now Lending</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">Purpose-built financing for short-term rental investors who want speed, flexibility, and simplicity.</p>
          </div>
          <div className="max-w-6xl mx-auto grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((b) => (
              <div key={b.title} className="bg-gray-50 rounded-xl p-6 border border-gray-100 hover:shadow-md hover:border-blue-100 transition-all duration-300" data-testid="str-benefit-card">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                  <b.icon className="h-6 w-6 text-blue-600" />
                </div>
                <h3 className="font-bold text-gray-900 mb-2">{b.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Loan Details Grid */}
      <section className="py-16 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto flex flex-col lg:flex-row gap-12">
            <div className="flex-1">
              <h2 className="text-base md:text-lg font-bold text-gray-900 mb-3">STR Loan Program Highlights</h2>
              <p className="text-gray-600 mb-8 leading-relaxed">
                Our short-term rental loan programs are designed specifically for investors operating on platforms like Airbnb and VRBO. Here's what you can expect.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {loanFeatures.map((f) => (
                  <div key={f.label} className="bg-white rounded-lg p-4 border border-gray-100" data-testid="str-loan-feature">
                    <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">{f.label}</p>
                    <p className="text-gray-900 font-semibold">{f.value}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Platforms Sidebar */}
            <div className="lg:w-80">
              <div className="bg-slate-900 rounded-2xl p-8 text-white sticky top-28">
                <h3 className="text-xl font-bold mb-2">Platforms We Finance</h3>
                <p className="text-gray-400 text-sm mb-6">We accept income from all major STR platforms.</p>
                <div className="space-y-3">
                  {platforms.map((p) => (
                    <div key={p.name} className="flex items-center gap-3 bg-white/10 rounded-lg px-4 py-3">
                      <MapPin className="h-4 w-4 text-blue-400 flex-shrink-0" />
                      <div>
                        <span className="font-semibold text-sm">{p.name}</span>
                        <span className="text-gray-400 text-xs ml-2">— {p.desc}</span>
                      </div>
                    </div>
                  ))}
                </div>
                <a href="https://rabbu.com" target="_blank" rel="noopener noreferrer">
                  <Button className="w-full mt-6 bg-blue-600 hover:bg-blue-700 text-white" data-testid="str-rabbu-cta">
                    Browse STR Properties on Rabbu <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-base md:text-lg font-bold text-gray-900 mb-3">How STR Financing Works</h2>
          </div>
          <div className="max-w-4xl mx-auto grid md:grid-cols-4 gap-6">
            {[
              { step: '01', title: 'Apply Online', desc: 'Fill out our quick form with your property and investment details.' },
              { step: '02', title: 'Get a Quote', desc: 'We analyze the STR income potential and provide a loan quote within 24 hours.' },
              { step: '03', title: 'Underwriting', desc: 'Our team handles the heavy lifting — appraisal, title, and rental analysis.' },
              { step: '04', title: 'Close & Earn', desc: 'Sign the docs, get your keys, and start earning rental income.' },
            ].map((item) => (
              <div key={item.step} className="text-center">
                <div className="w-14 h-14 bg-blue-100 text-blue-700 rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold">{item.step}</div>
                <h3 className="text-base font-bold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Inquiry Form */}
      <section className="py-16 bg-slate-900" id="str-inquiry-form">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-8">
              <h2 className="text-base md:text-lg font-bold text-white mb-3">Get Pre-Qualified for STR Financing</h2>
              <p className="text-gray-400">Tell us about your short-term rental investment and we'll get back to you within 24 hours with a loan quote.</p>
            </div>

            {success ? (
              <div className="bg-blue-900/40 border border-blue-500/30 rounded-xl p-8 text-center" data-testid="str-form-success">
                <CheckCircle className="h-12 w-12 text-blue-400 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-white mb-2">Application Received!</h3>
                <p className="text-gray-300 mb-4">A loan specialist will reach out within 24 hours to discuss your STR financing options.</p>
                <a href="tel:+19034762937">
                  <Button className="bg-blue-600 hover:bg-blue-700 text-white">
                    <Phone className="h-4 w-4 mr-2" />Call Us Now: 903-476-2937
                  </Button>
                </a>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="bg-white rounded-xl border border-gray-200 p-6 md:p-8 shadow-sm space-y-5" data-testid="str-inquiry-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <Label className="text-sm font-medium text-gray-700">First Name *</Label>
                    <Input name="firstName" value={formData.firstName} onChange={handleChange} required className="mt-1" data-testid="str-firstName" />
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-gray-700">Last Name *</Label>
                    <Input name="lastName" value={formData.lastName} onChange={handleChange} required className="mt-1" data-testid="str-lastName" />
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-gray-700">Email *</Label>
                    <Input name="email" type="email" value={formData.email} onChange={handleChange} required className="mt-1" data-testid="str-email" />
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-gray-700">Phone *</Label>
                    <Input name="phone" type="tel" value={formData.phone} onChange={handleChange} required className="mt-1" data-testid="str-phone" />
                  </div>
                </div>

                <div>
                  <Label className="text-sm font-medium text-gray-700">Property Address</Label>
                  <Input name="propertyAddress" value={formData.propertyAddress} onChange={handleChange} placeholder="Address of the STR property (if known)" className="mt-1" data-testid="str-propertyAddress" />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                  <div>
                    <Label className="text-sm font-medium text-gray-700">Purchase Price</Label>
                    <Input name="purchasePrice" type="number" placeholder="$" value={formData.purchasePrice} onChange={handleChange} className="mt-1" data-testid="str-purchasePrice" />
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-gray-700">Rehab Budget</Label>
                    <Input name="rehabBudget" type="number" placeholder="$" value={formData.rehabBudget} onChange={handleChange} className="mt-1" data-testid="str-rehabBudget" />
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-gray-700">ARV / After-Repair Value</Label>
                    <Input name="arv" type="number" placeholder="$" value={formData.arv} onChange={handleChange} className="mt-1" data-testid="str-arv" />
                  </div>
                </div>

                <div>
                  <Label className="text-sm font-medium text-gray-700">Exit Strategy</Label>
                  <Input name="exitStrategy" value={formData.exitStrategy} onChange={handleChange} placeholder="e.g., Hold as STR, Refinance, Sell" className="mt-1" data-testid="str-exitStrategy" />
                </div>

                <div>
                  <Label className="text-sm font-medium text-gray-700">Additional Information</Label>
                  <Textarea name="message" value={formData.message} onChange={handleChange} rows={3} placeholder="Tell us about your STR investment plans..." className="mt-1" data-testid="str-message" />
                </div>

                {error && <p className="text-red-600 text-sm" data-testid="str-form-error">{error}</p>}

                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    id="sms-optin-str"
                    checked={smsOptIn}
                    onChange={e => setSmsOptIn(e.target.checked)}
                    className="mt-1 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    required
                    data-testid="str-sms-optin"
                  />
                  <label htmlFor="sms-optin-str" className="text-xs text-gray-500 leading-relaxed">
                    I agree to be contacted by Close Now Lending Corp via call, email, and text for financial services. To opt out of text messages, you can reply 'stop' at any time or reply 'help' for assistance. You can also select the unsubscribe link in the emails. Message and data rates may apply. Message frequency may vary. <a href="/privacy" className="text-blue-600 underline">Privacy Policy</a> | <a href="/terms" className="text-blue-600 underline">Terms of Service</a>
                  </label>
                </div>

                <Button type="submit" disabled={loading} className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3" data-testid="str-submit">
                  {loading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Submitting...</> : 'Submit STR Loan Inquiry'}
                </Button>
              </form>
            )}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ShortTermRentalPage;
